/**
 * PasswordInput — shared password field with show/hide eye toggle.
 * Use this for EVERY password input: login, create user, reset.
 *
 * data-testid="candeza-password-toggle" is on the toggle button.
 */
import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { Input } from '@/components/common';

// ---------------------------------------------------------------------------
// Eye icons (inline SVG — no extra dep)
// ---------------------------------------------------------------------------

const EyeIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Container = styled.div`
  position: relative;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: color ${({ theme }) => theme.transitions.fast};
  z-index: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

// ---------------------------------------------------------------------------
// Props / component
// ---------------------------------------------------------------------------

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  error?: string;
  hint?: string;
}

/**
 * Shared password input with show/hide eye toggle.
 * Renders an accessible toggle button tagged `data-testid="candeza-password-toggle"`.
 *
 * Uses the common `<Input>` component with an overlay toggle button.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ error, hint, style, ...props }, ref) => {
    const [visible, setVisible] = useState(false);

    return (
      <Container>
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          error={error}
          hint={hint}
          // Right padding makes room for the toggle button
          style={{ paddingRight: 48, ...style }}
          {...props}
        />
        <ToggleButton
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          data-testid="candeza-password-toggle"
          tabIndex={0}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </ToggleButton>
      </Container>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
